import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { isOrderNumberForOrderId } from "./order-number.js";

const FrontdeskIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const FrontdeskMoneyFenSchema = Type.Integer({
  minimum: 0,
  maximum: Number.MAX_SAFE_INTEGER,
});
const FrontdeskCanonicalDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});
const FrontdeskBusinessDateSchema = Type.String({
  minLength: 10,
  maxLength: 10,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$",
});
const FrontdeskChineseLabelSchema = Type.String({ minLength: 1, maxLength: 80 });
const FrontdeskChineseExplanationSchema = Type.String({ minLength: 1, maxLength: 300 });

export const FrontdeskTableStateSchema = Type.Union([
  Type.Literal("IDLE"),
  Type.Literal("IN_USE"),
  Type.Literal("AWAITING_CLEANING"),
  Type.Literal("DISABLED"),
]);
export type FrontdeskTableState = Static<typeof FrontdeskTableStateSchema>;

export const FrontdeskBillingStateSchema = Type.Union([
  Type.Literal("RUNNING"),
  Type.Literal("PAUSED"),
  Type.Literal("ENDED"),
]);
export type FrontdeskBillingState = Static<typeof FrontdeskBillingStateSchema>;

export const FrontdeskOrderStatusSchema = Type.Union([
  Type.Literal("OPEN"),
  Type.Literal("SETTLED"),
  Type.Literal("REFUNDED"),
  Type.Literal("CONFLICT"),
]);
export type FrontdeskOrderStatus = Static<typeof FrontdeskOrderStatusSchema>;

export const FrontdeskOrderSyncStateSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("SYNCED"),
  Type.Literal("CONFLICT"),
  Type.Literal("FAILED"),
  Type.Literal("ISOLATED"),
]);
export type FrontdeskOrderSyncState = Static<typeof FrontdeskOrderSyncStateSchema>;

export const FrontdeskCollectionStateSchema = Type.Union([
  Type.Literal("NOT_COLLECTED"),
  Type.Literal("PARTIALLY_COLLECTED"),
  Type.Literal("COLLECTED"),
  Type.Literal("PENDING_RECONCILIATION"),
  Type.Literal("PENDING_MEMBER_DEBIT"),
  Type.Literal("MEMBER_RECEIVABLE"),
  Type.Literal("CONFLICT"),
]);
export type FrontdeskCollectionState = Static<typeof FrontdeskCollectionStateSchema>;

export const FrontdeskCleaningStateSchema = Type.Union([
  Type.Literal("NOT_REQUIRED"),
  Type.Literal("AWAITING_CLEANING"),
  Type.Literal("CLEANING_COMPLETED"),
]);
export type FrontdeskCleaningState = Static<typeof FrontdeskCleaningStateSchema>;

export const FrontdeskLightOffStatusSchema = Type.Union([
  Type.Literal("NOT_REQUIRED"),
  Type.Literal("PENDING"),
  Type.Literal("CONFIRMED"),
  Type.Literal("FAILED"),
  Type.Literal("UNKNOWN"),
]);
export type FrontdeskLightOffStatus = Static<typeof FrontdeskLightOffStatusSchema>;

export const FrontdeskLightOffEvidenceSchema = Type.Union([
  Type.Literal("MOCK"),
  Type.Literal("REAL_HARDWARE"),
]);
export type FrontdeskLightOffEvidence = Static<typeof FrontdeskLightOffEvidenceSchema>;

export const FrontdeskLightOffFactSchema = Type.Object(
  {
    status: FrontdeskLightOffStatusSchema,
    evidence: Type.Union([FrontdeskLightOffEvidenceSchema, Type.Null()]),
    updatedAt: Type.Union([FrontdeskCanonicalDateTimeSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type FrontdeskLightOffFact = Static<typeof FrontdeskLightOffFactSchema>;

export const FrontdeskOperatingBlockerSchema = Type.Object(
  {
    code: Type.String({
      minLength: 1,
      maxLength: 128,
      pattern: "^[A-Z][A-Z0-9_]{0,127}$",
    }),
    label: FrontdeskChineseLabelSchema,
    explanation: FrontdeskChineseExplanationSchema,
  },
  { additionalProperties: false },
);
export type FrontdeskOperatingBlocker = Static<typeof FrontdeskOperatingBlockerSchema>;

export const FrontdeskOperatingTableFactSchema = Type.Object(
  {
    state: FrontdeskTableStateSchema,
    revision: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type FrontdeskOperatingTableFact = Static<typeof FrontdeskOperatingTableFactSchema>;

export const FrontdeskOperatingSessionFactSchema = Type.Object(
  {
    sessionId: FrontdeskIdSchema,
    aggregateVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    startedAt: FrontdeskCanonicalDateTimeSchema,
    endedAt: Type.Union([FrontdeskCanonicalDateTimeSchema, Type.Null()]),
    billingState: FrontdeskBillingStateSchema,
    billingPausedAt: Type.Union([FrontdeskCanonicalDateTimeSchema, Type.Null()]),
    estimatedFeeFen: FrontdeskMoneyFenSchema,
  },
  { additionalProperties: false },
);
export type FrontdeskOperatingSessionFact = Static<typeof FrontdeskOperatingSessionFactSchema>;

export const FrontdeskOperatingOrderFactSchema = Type.Object(
  {
    orderId: FrontdeskIdSchema,
    orderNumber: Type.Optional(Type.String({ minLength: 1, maxLength: 128 })),
    businessDate: FrontdeskBusinessDateSchema,
    status: FrontdeskOrderStatusSchema,
    totalFen: FrontdeskMoneyFenSchema,
    paidFen: FrontdeskMoneyFenSchema,
    refundedFen: FrontdeskMoneyFenSchema,
    outstandingFen: FrontdeskMoneyFenSchema,
    syncState: FrontdeskOrderSyncStateSchema,
    collectionState: FrontdeskCollectionStateSchema,
  },
  { additionalProperties: false },
);
export type FrontdeskOperatingOrderFact = Static<typeof FrontdeskOperatingOrderFactSchema>;

/**
 * One table row from the Gateway's single-read frontdesk operating board.
 * Customer identity, pricing configuration and payment methods deliberately remain separate facts.
 */
export const FrontdeskOperatingFactReadModelSchema = Type.Object(
  {
    tenantId: FrontdeskIdSchema,
    storeId: FrontdeskIdSchema,
    tableId: FrontdeskIdSchema,
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    area: Type.String({ minLength: 1, maxLength: 80 }),
    factVersion: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    authoritativeAt: FrontdeskCanonicalDateTimeSchema,
    table: FrontdeskOperatingTableFactSchema,
    session: Type.Union([FrontdeskOperatingSessionFactSchema, Type.Null()]),
    order: Type.Union([FrontdeskOperatingOrderFactSchema, Type.Null()]),
    cleaningState: FrontdeskCleaningStateSchema,
    lightOff: FrontdeskLightOffFactSchema,
    statusLabel: FrontdeskChineseLabelSchema,
    statusExplanation: FrontdeskChineseExplanationSchema,
    nextStepLabel: FrontdeskChineseLabelSchema,
    blockers: Type.Array(FrontdeskOperatingBlockerSchema, { maxItems: 100 }),
  },
  { additionalProperties: false },
);
export type FrontdeskOperatingFactReadModel = Static<typeof FrontdeskOperatingFactReadModelSchema>;

export const FrontdeskOperatingFactReadModelListSchema = Type.Array(
  FrontdeskOperatingFactReadModelSchema,
  { maxItems: 10_000 },
);
export type FrontdeskOperatingFactReadModelList = Static<
  typeof FrontdeskOperatingFactReadModelListSchema
>;

const FrontdeskIdempotencyKeySchema = Type.String({ minLength: 8, maxLength: 128 });
const FrontdeskExpectedTableRevisionSchema = Type.Integer({
  minimum: 0,
  maximum: Number.MAX_SAFE_INTEGER,
});
const FrontdeskExpectedAggregateVersionSchema = Type.Integer({
  minimum: 1,
  maximum: Number.MAX_SAFE_INTEGER,
});

export const FrontdeskOpenSessionRequestSchema = Type.Object(
  {
    tableId: FrontdeskIdSchema,
    idempotencyKey: FrontdeskIdempotencyKeySchema,
    expectedTableRevision: FrontdeskExpectedTableRevisionSchema,
    customerRequestEventId: Type.Optional(Type.String({ minLength: 8, maxLength: 128 })),
    pricingPlanId: Type.Optional(
      Type.String({
        minLength: 1,
        maxLength: 64,
        pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
      }),
    ),
    expectedPricingPlanVersion: Type.Optional(
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    ),
  },
  { additionalProperties: false },
);
export type FrontdeskOpenSessionRequest = Static<typeof FrontdeskOpenSessionRequestSchema>;

export function isFrontdeskOpenSessionRequest(
  value: unknown,
): value is FrontdeskOpenSessionRequest {
  return (
    Value.Check(FrontdeskOpenSessionRequestSchema, value) &&
    (value.pricingPlanId === undefined) === (value.expectedPricingPlanVersion === undefined) &&
    (value.customerRequestEventId === undefined ||
      (value.customerRequestEventId.trim() === value.customerRequestEventId &&
        value.pricingPlanId !== undefined))
  );
}

const PrepaidOpenCanonicalTimestampSchema = Type.String({ format: "date-time" });
const PrepaidOpenSha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });

export const MemberPrepaidSessionOpenPreviewRequestSchema = Type.Object(
  {
    tableId: FrontdeskIdSchema,
    expectedTableRevision: FrontdeskExpectedTableRevisionSchema,
    pricingPlanId: Type.String({
      minLength: 1,
      maxLength: 64,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
    }),
    expectedPricingPlanVersion: Type.Integer({
      minimum: 1,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    memberId: Type.String({ minLength: 1, maxLength: 256 }),
  },
  { additionalProperties: false },
);
export type MemberPrepaidSessionOpenPreviewRequest = Static<
  typeof MemberPrepaidSessionOpenPreviewRequestSchema
>;

export const MemberPrepaidSessionOpenPreviewSchema = Type.Object(
  {
    tenantId: FrontdeskIdSchema,
    storeId: FrontdeskIdSchema,
    tableId: FrontdeskIdSchema,
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    expectedTableRevision: FrontdeskExpectedTableRevisionSchema,
    pricingPlanId: Type.String({ minLength: 1, maxLength: 64 }),
    pricingPlanName: Type.String({ minLength: 1, maxLength: 80 }),
    expectedPricingPlanVersion: Type.Integer({
      minimum: 1,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    billingMode: Type.Literal("PACKAGE"),
    paymentTiming: Type.Literal("PREPAID"),
    paymentMethod: Type.Literal("MEMBER_BALANCE"),
    proposedSessionId: FrontdeskIdSchema,
    proposedOrderId: FrontdeskIdSchema,
    memberId: Type.String({ minLength: 1, maxLength: 256 }),
    memberNumber: Type.String({ minLength: 1, maxLength: 64 }),
    memberDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    memberVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    walletVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    availableBalanceFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    prepaidFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    balanceAfterPrepaymentFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    walletCoveredAmountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    externalAmountFen: Type.Literal(0),
    memberSpendPlanHash: PrepaidOpenSha256Schema,
    refundableAmountFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    nonRefundableAmountFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    previewedAt: PrepaidOpenCanonicalTimestampSchema,
    expiresAt: PrepaidOpenCanonicalTimestampSchema,
    previewToken: PrepaidOpenSha256Schema,
  },
  { additionalProperties: false },
);
export type MemberPrepaidSessionOpenPreview = Static<typeof MemberPrepaidSessionOpenPreviewSchema>;

export const MemberPrepaidSessionOpenRequestSchema = Type.Object(
  {
    preview: MemberPrepaidSessionOpenPreviewSchema,
    customerRequestEventId: Type.Optional(Type.String({ minLength: 8, maxLength: 128 })),
    idempotencyKey: FrontdeskIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type MemberPrepaidSessionOpenRequest = Static<typeof MemberPrepaidSessionOpenRequestSchema>;

export function isMemberPrepaidSessionOpenPreview(
  value: unknown,
): value is MemberPrepaidSessionOpenPreview {
  if (!Value.Check(MemberPrepaidSessionOpenPreviewSchema, value)) return false;
  const preview = value as MemberPrepaidSessionOpenPreview;
  const previewedAt = Date.parse(preview.previewedAt);
  const expiresAt = Date.parse(preview.expiresAt);
  return (
    preview.paymentTiming === "PREPAID" &&
    preview.paymentMethod === "MEMBER_BALANCE" &&
    preview.billingMode === "PACKAGE" &&
    preview.walletCoveredAmountFen === preview.prepaidFen &&
    preview.externalAmountFen === 0 &&
    preview.balanceAfterPrepaymentFen === preview.availableBalanceFen - preview.prepaidFen &&
    preview.refundableAmountFen + preview.nonRefundableAmountFen === preview.prepaidFen &&
    Number.isFinite(previewedAt) &&
    Number.isFinite(expiresAt) &&
    expiresAt > previewedAt
  );
}

export function isMemberPrepaidSessionOpenRequest(
  value: unknown,
): value is MemberPrepaidSessionOpenRequest {
  if (!Value.Check(MemberPrepaidSessionOpenRequestSchema, value)) return false;
  const request = value as MemberPrepaidSessionOpenRequest;
  return (
    isMemberPrepaidSessionOpenPreview(request.preview) &&
    (request.customerRequestEventId === undefined ||
      request.customerRequestEventId.trim() === request.customerRequestEventId)
  );
}

export const FrontdeskClearTableRequestSchema = Type.Object(
  {
    idempotencyKey: FrontdeskIdempotencyKeySchema,
    expectedTableRevision: FrontdeskExpectedTableRevisionSchema,
    expectedSessionId: FrontdeskIdSchema,
  },
  { additionalProperties: false },
);
export type FrontdeskClearTableRequest = Static<typeof FrontdeskClearTableRequestSchema>;

const FrontdeskCloseoutRequestFields = {
  idempotencyKey: FrontdeskIdempotencyKeySchema,
  expectedTableRevision: FrontdeskExpectedTableRevisionSchema,
  expectedSessionId: FrontdeskIdSchema,
  expectedAggregateVersion: FrontdeskExpectedAggregateVersionSchema,
};

export const FrontdeskConfirmCleaningRequestSchema = Type.Object(FrontdeskCloseoutRequestFields, {
  additionalProperties: false,
});
export type FrontdeskConfirmCleaningRequest = Static<typeof FrontdeskConfirmCleaningRequestSchema>;

export const FrontdeskReleaseTableRequestSchema = Type.Object(FrontdeskCloseoutRequestFields, {
  additionalProperties: false,
});
export type FrontdeskReleaseTableRequest = Static<typeof FrontdeskReleaseTableRequestSchema>;

export const FrontdeskTableCloseoutEventTypeSchema = Type.Union([
  Type.Literal("TABLE_CLEANING_CONFIRMED"),
  Type.Literal("TABLE_RELEASED"),
]);
export type FrontdeskTableCloseoutEventType = Static<typeof FrontdeskTableCloseoutEventTypeSchema>;

const FrontdeskTableCloseoutReceiptFields = {
  tableId: FrontdeskIdSchema,
  sessionId: FrontdeskIdSchema,
  orderId: FrontdeskIdSchema,
  aggregateVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  tableRevision: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
  occurredAt: FrontdeskCanonicalDateTimeSchema,
};

/** TABLE_CLEARED remains a historical event and is not a current closeout receipt. */
export const FrontdeskTableCloseoutReceiptSchema = Type.Union([
  Type.Object(
    {
      eventType: Type.Literal("TABLE_CLEANING_CONFIRMED"),
      ...FrontdeskTableCloseoutReceiptFields,
      tableState: Type.Literal("AWAITING_CLEANING"),
      cleaningState: Type.Literal("CLEANING_COMPLETED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      eventType: Type.Literal("TABLE_RELEASED"),
      ...FrontdeskTableCloseoutReceiptFields,
      tableState: Type.Literal("IDLE"),
      cleaningState: Type.Literal("NOT_REQUIRED"),
    },
    { additionalProperties: false },
  ),
]);
export type FrontdeskTableCloseoutReceipt = Static<typeof FrontdeskTableCloseoutReceiptSchema>;

const internalStateTokens = [
  "IDLE",
  "IN_USE",
  "AWAITING_CLEANING",
  "DISABLED",
  "RUNNING",
  "PAUSED",
  "ENDED",
  "OPEN",
  "SETTLED",
  "REFUNDED",
  "CONFLICT",
  "PENDING",
  "SYNCED",
  "FAILED",
  "ISOLATED",
  "NOT_COLLECTED",
  "PARTIALLY_COLLECTED",
  "COLLECTED",
  "PENDING_RECONCILIATION",
  "PENDING_MEMBER_DEBIT",
  "MEMBER_RECEIVABLE",
  "NOT_REQUIRED",
  "CLEANING_COMPLETED",
  "CONFIRMED",
  "UNKNOWN",
  "MOCK",
  "REAL_HARDWARE",
] as const;

function isCanonicalDateTime(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

export function isFrontdeskTableCloseoutReceipt(
  value: unknown,
): value is FrontdeskTableCloseoutReceipt {
  return (
    Value.Check(FrontdeskTableCloseoutReceiptSchema, value) && isCanonicalDateTime(value.occurredAt)
  );
}

export function isFrontdeskTableCloseoutReceiptForRequest(
  value: unknown,
  request: FrontdeskConfirmCleaningRequest | FrontdeskReleaseTableRequest,
  expectedEventType: FrontdeskTableCloseoutEventType,
  expectedTableId: string,
): value is FrontdeskTableCloseoutReceipt {
  return (
    isFrontdeskTableCloseoutReceipt(value) &&
    value.eventType === expectedEventType &&
    value.tableId === expectedTableId &&
    value.sessionId === request.expectedSessionId &&
    value.aggregateVersion === request.expectedAggregateVersion + 1 &&
    value.tableRevision === request.expectedTableRevision + 1
  );
}

function isCanonicalBusinessDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function isPlainChineseCopy(value: string): boolean {
  return (
    value === value.trim() &&
    /\p{Script=Han}/u.test(value) &&
    internalStateTokens.every((token) => !value.includes(token))
  );
}

function isValidSessionTime(
  session: FrontdeskOperatingSessionFact,
  authoritativeAt: string,
): boolean {
  if (!isCanonicalDateTime(session.startedAt)) return false;
  const startedAt = Date.parse(session.startedAt);
  const capturedAt = Date.parse(authoritativeAt);
  if (startedAt > capturedAt) return false;

  if (session.billingState === "ENDED") {
    return (
      session.endedAt !== null &&
      isCanonicalDateTime(session.endedAt) &&
      Date.parse(session.endedAt) >= startedAt &&
      Date.parse(session.endedAt) <= capturedAt &&
      session.billingPausedAt === null
    );
  }
  if (session.endedAt !== null) return false;
  if (session.billingState === "PAUSED") {
    return (
      session.billingPausedAt !== null &&
      isCanonicalDateTime(session.billingPausedAt) &&
      Date.parse(session.billingPausedAt) >= startedAt &&
      Date.parse(session.billingPausedAt) <= capturedAt
    );
  }
  return session.billingPausedAt === null;
}

function hasValidMoney(order: FrontdeskOperatingOrderFact): boolean {
  if (
    ![order.totalFen, order.paidFen, order.refundedFen, order.outstandingFen].every(
      Number.isSafeInteger,
    ) ||
    order.paidFen > order.totalFen ||
    order.refundedFen > order.paidFen ||
    order.outstandingFen !== order.totalFen - order.paidFen
  ) {
    return false;
  }
  if (order.status === "SETTLED" || order.status === "REFUNDED") {
    if (order.outstandingFen !== 0) return false;
  }
  if (order.status === "REFUNDED" && order.refundedFen !== order.paidFen) return false;
  if (order.status === "SETTLED" && order.refundedFen > 0 && order.refundedFen === order.paidFen) {
    return false;
  }
  switch (order.collectionState) {
    case "NOT_COLLECTED":
      return order.paidFen === 0 && order.refundedFen === 0;
    case "PARTIALLY_COLLECTED":
      return (
        order.status === "OPEN" &&
        order.paidFen > 0 &&
        order.outstandingFen > 0 &&
        order.refundedFen === 0
      );
    case "COLLECTED":
      return order.outstandingFen === 0 && order.status !== "CONFLICT";
    case "PENDING_RECONCILIATION":
    case "PENDING_MEMBER_DEBIT":
    case "MEMBER_RECEIVABLE":
      return order.outstandingFen === 0 && order.status !== "CONFLICT";
    case "CONFLICT":
      return true;
  }
}

function hasValidLightOffFact(value: FrontdeskLightOffFact, authoritativeAt: string): boolean {
  if (value.status === "NOT_REQUIRED") {
    return value.evidence === null && value.updatedAt === null;
  }
  if (value.status === "UNKNOWN" && value.updatedAt === null) {
    return value.evidence === null;
  }
  if (
    value.updatedAt === null ||
    !isCanonicalDateTime(value.updatedAt) ||
    Date.parse(value.updatedAt) > Date.parse(authoritativeAt)
  ) {
    return false;
  }
  if (value.status === "CONFIRMED" || value.status === "FAILED") {
    return value.evidence !== null;
  }
  return true;
}

function hasValidRowState(value: FrontdeskOperatingFactReadModel): boolean {
  const { session, order } = value;
  if ((session === null) !== (order === null)) return false;
  if (session === null || order === null) {
    return (
      (value.table.state === "IDLE" || value.table.state === "DISABLED") &&
      value.cleaningState === "NOT_REQUIRED" &&
      value.factVersion === value.table.revision
    );
  }
  if (
    !isOrderNumberForOrderId(order.orderNumber, order.orderId, order.businessDate) ||
    !isValidSessionTime(session, value.authoritativeAt) ||
    !isCanonicalBusinessDate(order.businessDate) ||
    !hasValidMoney(order) ||
    session.estimatedFeeFen > order.totalFen
  ) {
    return false;
  }
  if (value.factVersion !== session.aggregateVersion) return false;
  const sourceConflict = order.status === "CONFLICT" || order.syncState === "CONFLICT";
  const collectionConflict = order.collectionState === "CONFLICT";
  if (sourceConflict && !collectionConflict) return false;
  if (collectionConflict) {
    if (value.blockers.length === 0 || value.nextStepLabel !== "暂停本桌经营并核对") {
      return false;
    }
    if (value.table.state === "IN_USE") {
      return (
        value.cleaningState === "NOT_REQUIRED" &&
        value.lightOff.status === "NOT_REQUIRED" &&
        (order.status === "OPEN" || order.status === "CONFLICT") &&
        (session.billingState === "RUNNING" ||
          session.billingState === "PAUSED" ||
          session.billingState === "ENDED")
      );
    }
    return (
      value.table.state === "AWAITING_CLEANING" &&
      (value.cleaningState === "AWAITING_CLEANING" ||
        value.cleaningState === "CLEANING_COMPLETED") &&
      session.billingState === "ENDED" &&
      session.endedAt !== null &&
      (order.status === "SETTLED" || order.status === "REFUNDED" || order.status === "CONFLICT") &&
      order.outstandingFen === 0
    );
  }
  if (value.table.state === "IN_USE") {
    return (
      value.cleaningState === "NOT_REQUIRED" &&
      value.lightOff.status === "NOT_REQUIRED" &&
      order.status === "OPEN" &&
      (session.billingState === "RUNNING" ||
        session.billingState === "PAUSED" ||
        session.billingState === "ENDED")
    );
  }
  if (value.table.state === "AWAITING_CLEANING") {
    return (
      (value.cleaningState === "AWAITING_CLEANING" ||
        value.cleaningState === "CLEANING_COMPLETED") &&
      session.billingState === "ENDED" &&
      session.endedAt !== null &&
      (order.status === "SETTLED" || order.status === "REFUNDED") &&
      order.outstandingFen === 0
    );
  }
  return false;
}

export function isFrontdeskOperatingFactReadModelList(
  value: unknown,
): value is FrontdeskOperatingFactReadModelList {
  if (!Value.Check(FrontdeskOperatingFactReadModelListSchema, value)) return false;
  if (value.length === 0) return true;

  const first = value[0];
  if (!first) return false;
  const tableIds = new Set<string>();
  const tableNumbers = new Set<string>();
  for (const fact of value) {
    if (
      fact.tenantId !== first.tenantId ||
      fact.storeId !== first.storeId ||
      fact.authoritativeAt !== first.authoritativeAt ||
      !isCanonicalDateTime(fact.authoritativeAt) ||
      !Number.isSafeInteger(fact.factVersion) ||
      !Number.isSafeInteger(fact.table.revision) ||
      !hasValidLightOffFact(fact.lightOff, fact.authoritativeAt) ||
      tableIds.has(fact.tableId) ||
      tableNumbers.has(fact.tableNumber) ||
      !isPlainChineseCopy(fact.statusLabel) ||
      !isPlainChineseCopy(fact.statusExplanation) ||
      !isPlainChineseCopy(fact.nextStepLabel) ||
      new Set(fact.blockers.map((blocker) => blocker.code)).size !== fact.blockers.length ||
      fact.blockers.some(
        (blocker) => !isPlainChineseCopy(blocker.label) || !isPlainChineseCopy(blocker.explanation),
      ) ||
      !hasValidRowState(fact)
    ) {
      return false;
    }
    tableIds.add(fact.tableId);
    tableNumbers.add(fact.tableNumber);
  }
  return true;
}
